import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
// Only the light modules statically: the corpus is dynamically imported in the loader.
import { SITE_URL } from "@/lib/site-url";
import { BLOG_INDEX_META, SUPPORTED_LANGS, isBlogLang } from "@/lib/blog-langs";
import type { BlogLang, BlogPost } from "@/lib/blog-types";

export const Route = createFileRoute("/$lang/blog/")({
  beforeLoad: ({ params }) => {
    if (!isBlogLang(params.lang)) throw notFound();
  },
  // The post list moved into the loader so `head` — which is synchronous and not
  // code-split — no longer needs a static import of the corpus.
  loader: async ({ params }) => {
    const lang = params.lang as BlogLang;
    const { getPostsByLang } = await import("@/lib/blog-posts");
    return { lang, posts: getPostsByLang(lang) };
  },
  head: ({ params, loaderData }) => {
    if (!isBlogLang(params.lang)) return { meta: [{ title: "Blog" }] };
    const lang = params.lang;
    const meta = BLOG_INDEX_META[lang];
    const url = `${SITE_URL}/${lang}/blog`;
    const posts = loaderData?.posts ?? [];
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { name: "keywords", content: meta.keywords },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:locale", content: lang === "es" ? "es_ES" : "en_US" },
        { property: "og:image", content: `${SITE_URL}/og/blog.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: meta.title },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
        { name: "twitter:image", content: `${SITE_URL}/og/blog.png` },
      ],
      links: [
        { rel: "canonical", href: url },
        ...SUPPORTED_LANGS.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/blog`,
        })),
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/blog` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: meta.title,
            url,
            inLanguage: lang,
            blogPost: posts.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              url: `${SITE_URL}/${lang}/blog/${p.slug}`,
              datePublished: p.date,
              inLanguage: lang,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: LocalizedBlogIndex,
});

function LocalizedBlogIndex() {
  // The loader already fetched the list; calling getPostsByLang() here would statically
  // re-import the corpus and undo the split.
  const { lang, posts } = Route.useLoaderData() as { lang: BlogLang; posts: BlogPost[] };
  const meta = BLOG_INDEX_META[lang];
  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          ←{" "}
          {lang === "es"
            ? "Volver al Dashboard"
            : lang === "fr"
              ? "Retour au Tableau de bord"
              : lang === "de"
                ? "Zurück zum Dashboard"
                : lang === "it"
                  ? "Torna alla Dashboard"
                  : lang === "pt"
                    ? "Voltar ao Painel"
                    : lang === "ru"
                      ? "Вернуться в Панель управления"
                      : lang === "zh"
                        ? "返回仪表板"
                        : "Back to Dashboard"}
        </Link>
      </div>
      <header className="mb-12 text-center">
        <p className="text-sm font-medium text-primary">SmsEmpire Blog</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">{meta.heading}</h1>
        <p className="mt-4 text-muted-foreground">{meta.sub}</p>
        <nav className="mt-6 flex justify-center gap-2 text-xs">
          {SUPPORTED_LANGS.map((l) => (
            <Link
              key={l}
              to="/$lang/blog"
              params={{ lang: l }}
              className={`rounded-full border px-3 py-1 ${
                l === lang
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.toUpperCase()}
            </Link>
          ))}
        </nav>
      </header>
      <div className="grid gap-6 sm:grid-cols-2">
        {posts.map((p) => (
          <Link
            key={p.slug}
            to="/$lang/blog/$slug"
            params={{ lang, slug: p.slug }}
            className="group rounded-2xl border border-border bg-card p-6 shadow-elegant transition hover:border-primary/40 hover:shadow-glow"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <time>{p.date}</time>
              <span>·</span>
              <span>{p.readTime}</span>
              {p.app && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
                  {p.app}
                </span>
              )}
            </div>
            <h2 className="mt-2 text-xl font-semibold text-foreground group-hover:text-primary">
              {p.title}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
              {meta.readCta} <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </main>
  );
}
